import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useForm } from "@formspree/react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seo";
import { FORMSPREE_FORM_ID } from "@/lib/formspree";
import { trackWhatsAppClick } from "@/lib/analytics";
import {
    WHATSAPP_HREF,
    EMAIL_HREF,
    STUDIO_EMAIL,
    SOCIALS,
} from "@/lib/links";

const STAGE_OPTIONS = [
    "Discover — strategy & positioning",
    "Strategy — messaging & roadmap",
    "Create — brand, website & content",
    "Launch — campaigns & activation",
    "Scale — data, AI & growth",
    "Not sure yet",
];

const SOCIAL_LINKS = [
    { label: "Instagram", href: SOCIALS.instagram, key: "instagram" },
    { label: "LinkedIn", href: SOCIALS.linkedin, key: "linkedin" },
    { label: "Facebook", href: SOCIALS.facebook, key: "facebook" },
];

const FAQS = [
    {
        q: "How do we start working together?",
        a: "Send us a message on WhatsApp or drop the form below. We'll set up a 20-minute call to understand where you are, share how we work, and figure out if we're a fit.",
    },
    {
        q: "How do you price engagements?",
        a: "We scope in three shapes: fixed-scope projects, monthly retainers, and 8-week growth sprints. Pricing depends on scope — but every proposal is a fixed number, not a mystery box.",
    },
    {
        q: "Do you work with early-stage founders?",
        a: "Yes. A large part of our work is with founders taking a brand from seed to launch — identity, website, first campaigns. If you're stubborn about your idea, we're your kind of studio.",
    },
    {
        q: "Which industries have you worked with?",
        a: "Hospitality, real estate, manufacturing, agriculture and food, SaaS, education and non-profits. We're sector-flexible, but taste-consistent.",
    },
    {
        q: "How fast can we launch?",
        a: "A website can go live in 4–6 weeks; a paid-media engine can start driving results within 30 days; a full rebrand typically takes 8–12 weeks. We move fast without cutting corners.",
    },
];

const ease = [0.22, 1, 0.36, 1];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        stage: STAGE_OPTIONS[0],
        budget: "",
        message: "",
    });
    const [openFaq, setOpenFaq] = useState(0);
    const [state, handleSubmit] = useForm(FORMSPREE_FORM_ID);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    useEffect(() => {
        if (state.succeeded) {
            toast.success(
                "Thanks for reaching out. We've received your enquiry and will get back to you within one business day."
            );
            setForm({
                name: "",
                email: "",
                company: "",
                stage: STAGE_OPTIONS[0],
                budget: "",
                message: "",
            });
        }
    }, [state.succeeded]);

    useEffect(() => {
        if (state.errors) {
            toast.error(
                "Something went wrong. Please try again or contact us directly at abhay@digitalbeej.in."
            );
        }
    }, [state.errors]);

    const onSubmit = (e) => {
        if (state.submitting) {
            e.preventDefault();
            return;
        }
        if (!form.name || !form.email || !form.message) {
            e.preventDefault();
            toast.error("Please fill in name, email and a short message.");
            return;
        }
        const ts = e.currentTarget.elements.namedItem("timestamp");
        if (ts) ts.value = new Date().toISOString();
        handleSubmit(e);
    };

    return (
        <>
            <Seo {...SEO.contact} />
            <PageHero
                data-testid="contact-page-hero"
                kicker="/ Contact · 05"
                meta="Avg. reply < 2 hrs"
                title="Let's"
                accent="plant it"
                tagline="Tell us where you are — we'll come to the first call with a point of view on your brand."
            />

            {/* Twin CTAs */}
            <section
                data-testid="contact-twin-ctas"
                className="relative py-8 lg:py-12"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                    <a
                        data-testid="contact-cta-whatsapp"
                        href={WHATSAPP_HREF}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                            trackWhatsAppClick({
                                section: "contact_page",
                                buttonName: "contact_page_whatsapp",
                            })
                        }
                        className="group relative bg-olive text-obsidian hover:bg-obsidian hover:text-bone transition-colors duration-300 p-6 lg:p-8 border border-black/10"
                    >
                        <div className="flex items-center justify-between mb-5">
                            <span className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">
                                Fastest reply
                            </span>
                            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">
                                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                Avg. reply &lt; 2 hrs
                            </span>
                        </div>
                        <div className="font-display font-bold text-2xl lg:text-3xl tracking-tight">
                            Chat on WhatsApp →
                        </div>
                        <p className="mt-3 text-sm opacity-70 max-w-xs">
                            For founders who want a fast, honest conversation —
                            no forms, no funnels.
                        </p>
                    </a>

                    <a
                        data-testid="contact-cta-email"
                        href={EMAIL_HREF}
                        className="group relative bg-transparent hover:bg-obsidian hover:text-bone transition-colors duration-300 p-6 lg:p-8 border border-black/25"
                    >
                        <div className="flex items-center justify-between mb-5">
                            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 group-hover:text-bone/55">
                                Prefer writing?
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/55 group-hover:text-bone/55">
                                &lt; 1 business day
                            </span>
                        </div>
                        <div className="font-display font-bold text-2xl lg:text-3xl tracking-tight">
                            {STUDIO_EMAIL} →
                        </div>
                        <p className="mt-3 text-sm text-black/65 group-hover:text-bone/65 max-w-xs">
                            Send us a few paragraphs about your brand — we
                            reply personally, not with a template.
                        </p>
                    </a>
                </div>
            </section>

            {/* Form + details */}
            <section
                data-testid="contact-form-section"
                className="relative py-16 lg:py-24"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-y-14 lg:gap-x-12">
                    {/* Studio details */}
                    <aside className="col-span-12 lg:col-span-4">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                            (Studio / 06)
                        </div>
                        <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-4xl lg:text-5xl">
                            The studio.
                        </h2>
                        <p className="mt-6 font-serif italic text-xl text-black/65 leading-snug max-w-sm">
                            Small, senior, and easy to reach — the way a studio
                            should be.
                        </p>

                        <div className="mt-10 space-y-8">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/45">
                                    Write to us
                                </div>
                                <a
                                    data-testid="contact-detail-email"
                                    href={EMAIL_HREF}
                                    className="mt-2 inline-block font-display font-bold text-xl lg:text-2xl link-underline"
                                >
                                    {STUDIO_EMAIL}
                                </a>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/45">
                                    WhatsApp
                                </div>
                                <a
                                    data-testid="contact-detail-whatsapp"
                                    href={WHATSAPP_HREF}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-block font-display font-bold text-xl lg:text-2xl link-underline"
                                >
                                    +91 89288 64286
                                </a>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/45">
                                    Studio
                                </div>
                                <div className="mt-2 text-lg text-black/80">
                                    Mumbai · India
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/45">
                                    Follow
                                </div>
                                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                                    {SOCIAL_LINKS.map((s) => (
                                        <a
                                            key={s.key}
                                            href={s.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`contact-detail-social-${s.key}`}
                                            className="text-sm font-medium link-underline"
                                        >
                                            {s.label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Form */}
                    <form
                        data-testid="contact-form"
                        onSubmit={onSubmit}
                        className="col-span-12 lg:col-span-8 lg:pl-6"
                    >
                        {/* Hidden tracking fields (not shown in UI) */}
                        <input
                            type="hidden"
                            name="_subject"
                            value="New Digitalbeej Website Enquiry"
                        />
                        <input type="hidden" name="source" value="digitalbeej.in" />
                        <input
                            type="hidden"
                            name="page"
                            value={
                                typeof window !== "undefined"
                                    ? window.location.href
                                    : ""
                            }
                        />
                        <input
                            type="hidden"
                            name="timestamp"
                        />
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55 mb-4">
                            (Enquiry form / 07)
                        </div>
                        <h2 className="font-display font-black uppercase tracking-tight leading-[0.9] text-4xl lg:text-5xl mb-10">
                            Tell us about your brand.
                        </h2>

                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-10">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                    Your name *
                                </label>
                                <input
                                    data-testid="contact-input-name"
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={update("name")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-black/25 focus:border-black outline-none py-3 text-lg placeholder-black/25 transition-colors"
                                    placeholder="Ada Lovelace"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                    Email *
                                </label>
                                <input
                                    data-testid="contact-input-email"
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-black/25 focus:border-black outline-none py-3 text-lg placeholder-black/25 transition-colors"
                                    placeholder="you@brand.com"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                    Company / brand
                                </label>
                                <input
                                    data-testid="contact-input-company"
                                    type="text"
                                    name="company"
                                    value={form.company}
                                    onChange={update("company")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-black/25 focus:border-black outline-none py-3 text-lg placeholder-black/25 transition-colors"
                                    placeholder="Your brand"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                    Where are you in the process?
                                </label>
                                <select
                                    data-testid="contact-input-stage"
                                    name="stage"
                                    value={form.stage}
                                    onChange={update("stage")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-black/25 focus:border-black outline-none py-3 text-lg text-black transition-colors appearance-none"
                                >
                                    {STAGE_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                    Rough budget (optional)
                                </label>
                                <input
                                    data-testid="contact-input-budget"
                                    type="text"
                                    name="budget"
                                    value={form.budget}
                                    onChange={update("budget")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-black/25 focus:border-black outline-none py-3 text-lg placeholder-black/25 transition-colors"
                                    placeholder="e.g. ₹5–15 lakhs / project · Monthly / retainer"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-black/50">
                                    Tell us about your brand *
                                </label>
                                <textarea
                                    data-testid="contact-input-message"
                                    rows={5}
                                    name="message"
                                    value={form.message}
                                    onChange={update("message")}
                                    className="mt-2 w-full bg-transparent border-0 border-b border-black/25 focus:border-black outline-none py-3 text-lg placeholder-black/25 resize-none transition-colors"
                                    placeholder="A few lines about your brand, goals, timeline & what a good outcome looks like…"
                                />
                            </div>
                        </div>

                        <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
                            <p className="text-xs text-black/45 max-w-sm">
                                Your details stay between us. No newsletter
                                subscriptions, no spam — ever.
                            </p>
                            <button
                                data-testid="contact-submit"
                                type="submit"
                                disabled={state.submitting}
                                className="group inline-flex items-center gap-3 bg-obsidian text-bone px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-olive hover:text-obsidian transition-colors duration-300 disabled:opacity-60"
                            >
                                {state.submitting ? "Sending…" : "Send enquiry"}
                                <span
                                    aria-hidden
                                    className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                                >
                                    →
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </section>

            {/* FAQ */}
            <section
                data-testid="contact-faq"
                className="relative py-24 lg:py-32 bg-[#F5EFE1]"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-y-10 lg:gap-x-12">
                    <div className="col-span-12 lg:col-span-4">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                            (Common questions / 08)
                        </div>
                        <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-4xl lg:text-5xl">
                            Before you write to us.
                        </h2>
                        <p className="mt-6 font-serif italic text-lg lg:text-xl text-black/60 max-w-sm">
                            A few things we get asked most often.
                        </p>
                    </div>

                    <div className="col-span-12 lg:col-span-8">
                        <ul className="border-t border-black/15">
                            {FAQS.map((f, i) => {
                                const open = openFaq === i;
                                return (
                                    <li
                                        key={f.q}
                                        data-testid={`faq-item-${i}`}
                                        className="border-b border-black/15"
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpenFaq(open ? -1 : i)
                                            }
                                            className="w-full flex items-start justify-between gap-6 py-6 text-left group"
                                        >
                                            <span className="font-display font-bold text-lg lg:text-2xl tracking-tight leading-snug">
                                                {f.q}
                                            </span>
                                            <span
                                                aria-hidden
                                                className={`shrink-0 mt-2 text-2xl transition-transform duration-500 text-[#2E5019] ${
                                                    open ? "rotate-45" : ""
                                                }`}
                                            >
                                                +
                                            </span>
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{
                                                height: open ? "auto" : 0,
                                                opacity: open ? 1 : 0,
                                            }}
                                            transition={{
                                                duration: 0.5,
                                                ease,
                                            }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 pr-10 text-black/70 leading-relaxed">
                                                {f.a}
                                            </p>
                                        </motion.div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </section>
        </>
    );
}
